import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	FormattedText,
	Form,
	FormItem,
	TextInput,
	ValidationError,
	SecretValueInput,
	Toolbar
} from "client/Components";
import { useFormHandler, IFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength,
	isUrl
} from "client/Validation/rules";
import {
	ZermeloConnectionSaveModel,
	create,
	update,
	get,
	ZermeloConnectionListItemModel
} from "client/Api/ZermeloConfiguration/zermeloConfiguration";
import { SecretValue } from "client/Api/Apps/common";
import { IObjectFormReducer } from "client/Forms/reducer";
import { AlertManager, useAlerts} from "client/Hooks";

export type ZermeloConnectionDialogProps = {
	connectionId?: number;
	onAfterSave(item: ZermeloConnectionListItemModel): void;
	onClose(): void;
};

const validationConfiguration: IValidationConfiguration<ZermeloConnectionSaveModel> = createValidationConfiguration<ZermeloConnectionSaveModel>();

validationConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("serverUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		})
	);

export const ZermeloConnectionDialog = (props: ZermeloConnectionDialogProps): ReactElement => {
	const {
		connectionId,
		onAfterSave,
		onClose
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();

	const [model, setModel] = useState<ZermeloConnectionSaveModel>({
		name: "",
		serverUrl: "",
		apiKey: {
			containsNewValue: true,
			value: ""
		}
	});
	const formHandler: IFormHandler<ZermeloConnectionSaveModel> = useFormHandler<ZermeloConnectionSaveModel>(
		model,
		setModel,
		validationConfiguration
	);

	useEffect(
		() => {
			if (connectionId === undefined) {
				return undefined;
			}

			const loadConnection = async (signal: AbortSignal): Promise<void> => {
				setIsLoading(true);

				try {
					const { name, serverUrl } = await get(
						connectionId,
						signal
					);
					setModel({
						name,
						serverUrl,
						apiKey: {
							containsNewValue: false,
							value: ""
						}
					});

					setIsLoading(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			loadConnection(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, connectionId, onClose]
	);

	const isNewConnection: boolean = connectionId === undefined;

	let titleKey: LocalizationKey;
	let saveKey: LocalizationKey;
	if (isNewConnection) {
		titleKey = "_NewNameConnection";
		saveKey = "_Add";
	} else {
		titleKey = "_EditNameConnection";
		saveKey = "_Save";
	}

	const submitHandler = async (): Promise<void> => {
		if (isSaving) {
			return;
		}

		const validationResult: InputValidationResult = await formHandler.validation.validate();
		if (validationResult.kind === InputValidationResultKind.Invalid) {
			return;
		}

		setIsSaving(true);

		let id: number | undefined = connectionId;
		try {
			if (id === undefined) {
				id = (await create(model)).connectionId;
			} else {
				await update(id, model);
			}

			onClose();
			onAfterSave({
				id,
				name: model.name,
				serverUrl: model.serverUrl
			});
		} catch (e) {
			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_ChangesNotSaved"));
			}
			setIsSaving(false);
		}
	};

	const enforceToSupplyApiKey = (): void => {
		const apiKeyHandler: IObjectFormReducer<SecretValue> = formHandler.reducer.for("apiKey");
		if (apiKeyHandler.value.containsNewValue) {
			return;
		}

		apiKeyHandler.setValue({
			containsNewValue: true,
			value: ""
		});
	};

	return (
		<Dialog hasBusyIndicator={isLoading || isSaving} trackingId="zermelo-connection-dialog">
			<Dialog.Header
				title={
					<FormattedText
						text={localize(titleKey)}
						thingsToReplace={[
							{ what: "{{Name}}", filler: localize("_Zermelo")}
						]}
					/>
				}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_Name")}>
						<TextInput {...formHandler.for("name").attachToTextInput()} />
						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ServerUrl")}>
						<TextInput {
							...formHandler.for("serverUrl")
								.attachToTextInput()
								.withOnChange(enforceToSupplyApiKey)
						} />
						<ValidationError {...formHandler.for("serverUrl").attachToValidationError()} />
					</FormItem>

					<FormItem label={localize("_ApiKey")}>
						<SecretValueInput
							{...formHandler.for("apiKey").attachToSecretValueInput()}
							containsInitialValue={!isNewConnection}
						/>
						<ValidationError {
							...formHandler.for("apiKey")
								.for("value")
								.attachToValidationError()
						} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(saveKey)}
						onClick={submitHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ZermeloConnectionDialog.displayName = "ZermeloConnectionDialog";
